import { useState } from "react";
import { Box, FileText, List, Table as TableIcon, X } from "lucide-react";

type RepositoryRow = {
  id: string;
  part: string;
  desc: string;
  supplier: string;
  docs: string;
  stage: string;
  qvm: string;
  status: string;
  updated: string;
  qvmPos: boolean | null;
};

const REPOSITORY_ROWS: RepositoryRow[] = [
  {
    id: "CM-2026-0142",
    part: "EVH-4420-A3",
    desc: "EV Battery Housing Bracket",
    supplier: "CastPro Industries",
    docs: "5/6",
    stage: "Correlation",
    qvm: "+14.6%",
    status: "In Review",
    updated: "Aug 3, 2026",
    qvmPos: false,
  },
  {
    id: "CM-2026-0138",
    part: "EVH-2210-B1",
    desc: "Busbar Support Casting",
    supplier: "Alucraft SA",
    docs: "6/6",
    stage: "Closed",
    qvm: "-1.4%",
    status: "Approved",
    updated: "Jul 24, 2026",
    qvmPos: true,
  },
  {
    id: "CM-2026-0131",
    part: "CHS-9080",
    desc: "Coolant Manifold",
    supplier: "NovaFlow Tech",
    docs: "4/6",
    stage: "Classification",
    qvm: "—",
    status: "Awaiting Inputs",
    updated: "Jul 21, 2026",
    qvmPos: null,
  },
  {
    id: "CM-2026-0127",
    part: "BRK-5512",
    desc: "Motor Mount Bracket",
    supplier: "CastPro Industries",
    docs: "6/6",
    stage: "Closed",
    qvm: "+11.8%",
    status: "Renegotiated",
    updated: "Jul 15, 2026",
    qvmPos: false,
  },
  {
    id: "CM-2026-0119",
    part: "HSG-3301",
    desc: "Inverter Housing",
    supplier: "Precision Die MX",
    docs: "6/6",
    stage: "Closed",
    qvm: "+4.2%",
    status: "Approved",
    updated: "Jul 08, 2026",
    qvmPos: false,
  },
];

const REPOSITORY_DOCS = [
  { type: "2D Drawing (PDF / DWG)", file: "EVH-4420-A3_rev-C.pdf", icon: FileText },
  { type: "3D Model (STEP / CATIA)", file: "EVH-4420-A3.step", icon: Box },
  { type: "BOM / Indented Structure", file: "EVH-4420_BOM.xlsx", icon: List },
  { type: "Technical Spec / PPAP Req.", file: "EVH-4420_tech-spec.pdf", icon: FileText },
  { type: "Input Template (Cost)", file: "cost-input-template.xlsx", icon: TableIcon },
];

export function RepositoryStep() {
  const [selectedRepoRow, setSelectedRepoRow] = useState<string | null>(null);
  const statusClassMap: Record<string, string> = {
    "In Review": "repo-pill--status-review",
    Approved: "repo-pill--status-approved",
    "Awaiting Inputs": "repo-pill--status-awaiting",
    Renegotiated: "repo-pill--status-renegotiated",
  };
  const stageClassMap: Record<string, string> = {
    Correlation: "repo-pill--stage-correlation",
    Closed: "repo-pill--stage-closed",
    Classification: "repo-pill--stage-classification",
  };

  return (
    <div className="repo-step">
      <div className="repo-subtab-row">
        <button type="button" className="repo-subtab-btn">
          Request &amp; History
        </button>
      </div>

      <div className="repo-insight">
        <div className="repo-insight__head">
          <div className="repo-insight__ai-badge">
            <p className="repo-insight__ai-text">AI</p>
          </div>
          <p className="repo-insight__title">Portfolio Insight</p>
        </div>
        <p className="repo-insight__body">
          The live request CM-2026-0142 shows a 15.7% gap to should-cost, the widest open item. CastPro appears on two of the five
          open requests with a consistent tooling premium - a supplier-level tooling framework would be worth more than negotiating each
          part individually.
        </p>
      </div>

      <div className="repo-kpi-grid">
        {[
          { label: "OPEN REQUESTS", value: "3", sub: "2 closed this month", isHighlight: false },
          { label: "PARTS MODELLED YTD", value: "128", sub: "AI should-cost", isHighlight: false },
          { label: "SAVINGS IDENTIFIED", value: "$1.42M", sub: "$0.86M realised", isHighlight: false },
          { label: "AVG. CYCLE TIME", value: "2.4 d", sub: "was 11 d manual", isHighlight: true },
        ].map(({ label, value, sub, isHighlight }) => (
          <div key={label} className={`repo-kpi-card${isHighlight ? " repo-kpi-card--highlight" : ""}`}>
            <p className="repo-kpi-card__label">{label}</p>
            <p className="repo-kpi-card__value">{value}</p>
            <p className="repo-kpi-card__sub">{sub}</p>
          </div>
        ))}
      </div>

      <div className="repo-panel">
        <div className="repo-panel__head">
          <p className="repo-panel__title">Request tracker</p>
          <div className="repo-priority-badge-wrap">
            <p className="repo-priority-badge">AI-PRIORITISED</p>
          </div>
        </div>
        <table className="repo-table">
          <thead>
            <tr>
              {["REQUEST", "PART", "DESCRIPTION", "SUPPLIER", "DOCUMENTS", "STAGE", "QUOTE VS MODEL", "STATUS", "UPDATED"].map(
                (h) => (
                  <th key={h} className="repo-table__th">
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {REPOSITORY_ROWS.map((row) => {
              const isSelected = selectedRepoRow === row.id;
              return (
                <tr
                  key={row.id}
                  className={`repo-table__row ${isSelected ? "is-selected" : ""}`}
                  onClick={() => setSelectedRepoRow(isSelected ? null : row.id)}
                >
                  <td className="repo-table__td repo-table__td--id">{row.id}</td>
                  <td className="repo-table__td repo-table__td--strong">{row.part}</td>
                  <td className="repo-table__td repo-table__td--desc">{row.desc}</td>
                  <td className="repo-table__td repo-table__td--base">{row.supplier}</td>
                  <td className="repo-table__td repo-table__td--base">{row.docs}</td>
                  <td className="repo-table__td">
                    <span
                      className={`repo-pill ${stageClassMap[row.stage] ?? "repo-pill--neutral"}`}
                    >
                      {row.stage}
                    </span>
                  </td>
                  <td className="repo-table__td">
                    {row.qvmPos === null ? (
                      <span className="repo-qvm--empty">-</span>
                    ) : (
                      <span className={`repo-qvm ${row.qvmPos ? "repo-qvm--positive" : "repo-qvm--negative"}`}>
                        {row.qvm}
                      </span>
                    )}
                  </td>
                  <td className="repo-table__td">
                    <span className={`repo-pill ${statusClassMap[row.status] ?? "repo-pill--neutral"}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="repo-table__td repo-table__td--updated">{row.updated}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedRepoRow === "CM-2026-0142" && (
        <div className="repo-panel">
          <div className="repo-panel__head repo-panel__head--docs">
            <p className="repo-panel__title repo-panel__title--docs">Documents - EVH-4420-A3</p>
            <button
              type="button"
              onClick={() => setSelectedRepoRow(null)}
              className="repo-close-btn"
            >
              <X size={13} />
            </button>
          </div>
          <table className="repo-table">
            <thead>
              <tr>
                {["DOCUMENT", "FILE", "STATUS"].map((h) => (
                  <th key={h} className="repo-table__th repo-table__th--docs">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {REPOSITORY_DOCS.map(({ type, file, icon: Icon }) => (
                <tr key={type} className="repo-table__row">
                  <td className="repo-table__td repo-table__td--docs">
                    <div className="repo-doc-type-wrap">
                      <Icon size={16} className="repo-doc-type-wrap__icon" />
                      <p className="repo-doc-type-wrap__text">{type}</p>
                    </div>
                  </td>
                  <td className="repo-table__td repo-table__td--file">{file}</td>
                  <td className="repo-table__td repo-table__td--docs">
                    <span className="repo-pill repo-pill--status-approved">
                      Parsed
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
